/* eslint-disable react/no-array-index-key */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  FILTER_CONFIG,
  FILTER_OPTIONS,
  FILTER_TYPES,
  MAX_FILTERS,
} from 'modules/scenario/constants';
import { FILTER_BOX } from '../style';
import DateRangeFilter from '../filters/date-range-filter';
import BreakdownFilter from '../filters/breakdown-filter';
import FocusOnFilter from '../filters/focus-on-filter';
import HotelFilter from '../filters/hotel-filter';
import BookingPaceHorizonFilter from '../filters/booking-pace-horizon-filter';
import PercentageFilter from '../filters/percentage-filter';
import ComparisonPeriodFilter from '../filters/comparison-period-filter';
import ComparisonAlignmentFilter from '../filters/comparison-alignment';

/* Modal used to add and manage filters for the selected new visual.
 * Props:
 * @param {boolean} open - Controls modal visibility
 * @param {Function} onClose - Function to close the modal
 * @param {Object} selectedVisual - Currently selected visual object
 * @param {Object} filters - Existing filters grouped by visual
 * @param {Function} setFilters - State setter to update filters
 */
const FilterModal = ({ open, onClose, selectedVisual, filters, setFilters }) => {
  const [filterInputs, setFilterInputs] = useState([]);
  // Load existing filters
  useEffect(() => {
    if (!selectedVisual) return;
    const visualFilters = filters?.[selectedVisual?.id];

    if (visualFilters) {
      const mapped = Object.entries(visualFilters).map(([name, value]) => ({
        name,
        keyword: value.keyword || '',
        value: value.value || '',
      }));

      setFilterInputs(mapped);
    } else {
      setFilterInputs([{ name: '', keyword: '', value: '' }]);
    }
  }, [selectedVisual, filters]);

  const handleChange = (index, field, value) => {
    const updated = [...filterInputs];
    updated[index][field] = value;
    setFilterInputs(updated);
  };

  const addFilterInput = () => {
    if (filterInputs.length >= MAX_FILTERS) return;

    setFilterInputs([...filterInputs, { name: '', keyword: '', value: '' }]);
  };

  const saveFilters = () => {
    const visualFilterObject = {};

    filterInputs.forEach((f) => {
      if (f.name) {
        visualFilterObject[f.name] = {
          keyword: f.keyword,
          value: f.value,
        };
      }
    });

    setFilters((prev) => ({
      ...prev,
      [selectedVisual?.id]: visualFilterObject,
    }));

    onClose();
  };
  const handleDropDownChange = (index, field, oldValue, value) => {
    if (oldValue) {
      const updated = [...filterInputs].map((item) => {
        if (item.name === oldValue) {
          return { ...item, keyword: '', value: '' };
        }
        return item;
      });
      updated[index][field] = value;
      setFilterInputs(updated);
    } else {
      const updated = [...filterInputs];
      updated[index][field] = value;
      setFilterInputs(updated);
    }
  };

  const renderFilterInput = (filter, index) => {
    const config = FILTER_CONFIG[filter.name];

    if (!config) return null;

    switch (config.type) {
      case FILTER_TYPES.DATE_RANGE:
        return (
          <DateRangeFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      case FILTER_TYPES.BREAKDOWN:
        return (
          <BreakdownFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      case FILTER_TYPES.FOCUS_ON:
        return (
          <FocusOnFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      case FILTER_TYPES.HOTEL:
        return (
          <HotelFilter value={filter.value} onChange={(val) => handleChange(index, 'value', val)} />
        );
      case FILTER_TYPES.BOOKING_PACE_HORIZON:
        return (
          <BookingPaceHorizonFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      case FILTER_TYPES.PERCENTAGE:
        return (
          <PercentageFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      case FILTER_TYPES.COMPARISON_PERIOD:
        return (
          <ComparisonPeriodFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      case FILTER_TYPES.COMPARISON_ALIGNMENT:
        return (
          <ComparisonAlignmentFilter
            value={filter.value}
            onChange={(val) => handleChange(index, 'value', val)}
          />
        );
      default:
        return (
          <TextField
            label="Value"
            value={filter.value || ''}
            onChange={(e) => handleChange(index, 'value', e.target.value)}
            fullWidth
            size="small"
          />
        );
    }
  };
  const isAnyFieldEmpty = filterInputs.some((f) => !f.name || !f.keyword || !f.value);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{`Filters of ${selectedVisual?.displayName} Visual`}</DialogTitle>

      <DialogContent>
        {filterInputs.map((filter, index) => (
          <Box key={`${filter?.name}_${index}`} sx={FILTER_BOX}>
            <TextField
              select
              label="Filter Name"
              value={filter.name}
              onChange={(e) => {
                handleDropDownChange(index, 'name', filter.name, e.target.value);
              }}
              fullWidth
              size="small"
              sx={{ mb: 2.5 }}
            >
              {FILTER_OPTIONS.filter((option) => {
                const selectedValues = filterInputs
                  .map((f) => f.name)
                  .filter((_, idx) => idx !== index);

                return !selectedValues.includes(option.value);
              }).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Keyword"
              value={filter.keyword}
              onChange={(e) => handleChange(index, 'keyword', e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2.5 }}
            />

            {renderFilterInput(filter, index)}
          </Box>
        ))}

        {filterInputs.length < MAX_FILTERS && (
          <Button variant="outlined" onClick={addFilterInput} disabled={isAnyFieldEmpty}>
            Add Filter
          </Button>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={saveFilters} disabled={isAnyFieldEmpty}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
